/**
 * The monthly flow — SPEC §9.1.
 *
 * The three taps, in order: **Apply my usual pattern** → review → **Submit** → **Confirm**.
 *
 * The governing rule is that the pattern is never submitted sight-unseen. "Apply my usual
 * pattern" is a *fill*: it puts concrete dates on the screen and stops. Review is therefore
 * unavoidable rather than an optional extra step, because the dates are sitting in front of
 * the instructor and nothing has been sent.
 *
 * Plain language throughout (SPEC §12.2) — the words *cycle*, *submission* and *partial*
 * never reach the screen.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  compare,
  formatDate,
  isOnLeaveOn,
  isProvisional,
  parseISO,
  splitByHorizon,
  type CalendarDate,
} from '@gearup/shared';
import {
  ApiError,
  api,
  type AvailabilityApi,
  horizonContextOf,
  type ExceptionInput,
  type LeaveView,
  type LocationOption,
  type MonthView,
  type Occurrence,
} from '../lib/api.js';
import { MonthCalendar, formatDayLong } from '../components/MonthCalendar.js';
import { DayDetail, describeSlot } from '../components/DayDetail.js';
import { ConfirmedThrough } from '../components/ConfirmedThrough.js';
import { LeaveNotice } from '../components/LeaveNotice.js';
import { DefaultsReview } from '../components/DefaultsReview.js';

type Tone = 'info' | 'success' | 'warn' | 'error';

/**
 * Words that change depending on who is looking.
 *
 * The screen is the same one either way — same rules, same server code, same failure modes
 * — but "your usual days" reads as nonsense when Luke is entering somebody else's month for
 * them, and getting that wrong is how an admin misreads whose availability they are about
 * to submit.
 */
export interface Voice {
  /** Empty when the instructor is looking at their own month. */
  subjectName: string;
  possessive: string;
  subject: string;
}

const OWN_VOICE: Voice = { subjectName: '', possessive: 'your', subject: 'you' };

export function Availability({
  availability = api.availability,
  leaves: leavesFor = () => api.leave.list(),
  voice = OWN_VOICE,
}: {
  availability?: AvailabilityApi;
  /**
   * Whose breaks to show. MUST follow the subject, not the signed-in session.
   *
   * The default reads `/api/profile/leave`, which is the SIGNED-IN INSTRUCTOR'S breaks. On
   * the on-behalf screen that is the wrong person: Luke, signed in as both an admin and an
   * instructor in the same browser, saw his own break rendered under Cleo's name, striking
   * out her days and taking them out of her count. Nothing on screen said whose it was.
   */
  leaves?: () => Promise<{ leaves: LeaveView[] }>;
  voice?: Voice;
} = {}) {
  const [months, setMonths] = useState<MonthView[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [today, setToday] = useState<CalendarDate | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<{ tone: Tone; text: string } | null>(null);
  const [leaves, setLeaves] = useState<LeaveView[]>([]);

  const view = useMemo(() => months.find((m) => m.cycleKey === active) ?? null, [months, active]);

  const load = useCallback(async () => {
    const [{ months: loaded, requiredCycle }, meta, breaks] = await Promise.all([
      availability.months(),
      availability.locations(),
      // Never fatal: a failure here must not stop somebody entering availability, which is
      // the job this page exists for.
      leavesFor().catch(() => ({ leaves: [] as LeaveView[] })),
    ]);
    setMonths(loaded);
    setLocations(meta.locations);
    setToday(meta.today);
    // ALL of them, not just the first. Somebody can book two breaks in different months —
    // the database allows it as long as they do not overlap — and keeping only one left the
    // second one's month completely unmarked while the first one's was struck through.
    setLeaves(breaks.leaves.filter((l) => l.endedOn === null));
    setActive((current) => current ?? requiredCycle ?? loaded[0]?.cycleKey ?? null);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load().catch(() => setLoading(false));
  }, [load]);

  const replace = (updated: MonthView) =>
    setMonths((all) => all.map((m) => (m.cycleKey === updated.cycleKey ? updated : m)));

  /**
   * Exceptions are derived from the current on-screen state rather than accumulated as the
   * instructor clicks. Sending the whole picture each time means a dropped request or a
   * double-tap cannot leave the server holding a half-applied set of edits.
   *
   * ## Per SLOT, not per date
   *
   * The first version emitted one addition per date, picked with `.find`. That silently
   * dropped a second class on the same day — which is exactly Donald's schedule, a morning
   * and an afternoon at the same site. The database was always shaped for it
   * (`AvailabilityDate` is unique on instructor+date+location+period); this function was
   * where it got lost.
   *
   * A day whose slots match the pattern emits nothing. A day that differs emits a REMOVAL
   * plus one ADDITION per surviving slot — REMOVAL is date-level by design ("I'm out on the
   * 14th" means the whole day) and additions survive it, so the two compose exactly right.
   */
  const exceptionsFor = (month: MonthView, occurrences: Occurrence[]): ExceptionInput[] => {
    const home = locations.find((l) => l.isHome) ?? locations[0];

    const group = (list: Occurrence[]) => {
      const map = new Map<string, Occurrence[]>();
      for (const o of list) map.set(o.date, [...(map.get(o.date) ?? []), o]);
      return map;
    };

    const shownByDate = group(occurrences);
    const patternByDate = month.patternApplied ? group(month.patternPreview) : new Map();

    const signature = (list: Occurrence[]) =>
      list
        .map((o) => `${o.locationId}|${o.period}|${o.startTime ?? ''}`)
        .sort()
        .join(',');

    const out: ExceptionInput[] = [];
    for (const date of new Set([...shownByDate.keys(), ...patternByDate.keys()])) {
      const shown = shownByDate.get(date) ?? [];
      const fromPattern: Occurrence[] = patternByDate.get(date) ?? [];

      // Untouched pattern day: the pattern already produces exactly this, so recording it
      // as an exception would freeze it against later pattern edits.
      if (signature(shown) === signature(fromPattern)) continue;

      if (fromPattern.length > 0) out.push({ date, type: 'REMOVAL' });

      for (const o of shown) {
        out.push({
          date,
          type: 'ADDITION',
          locationId: o.locationId || home?.id || 0,
          period: o.period,
          ...(o.startTime ? { startTime: o.startTime } : {}),
        });
      }
    }
    return out;
  };

  /**
   * Identity for one slot on screen. Mirrors the database's unique key
   * (instructor, date, location, period) plus the time, so two classes on one day are
   * addressable separately rather than collapsing into "the Saturday".
   */
  const slotKey = (o: Occurrence) =>
    `${o.date}|${o.locationId}|${o.period}|${o.startTime ?? ''}`;

  /** Persist a whole new set of occurrences, optimistically. */
  async function persist(next: Occurrence[]) {
    if (!view) return;
    const previous = view;
    replace({ ...view, occurrences: next });
    setConfirming(false);
    try {
      replace(
        await availability.saveDraft(previous.cycleKey, {
          exceptions: exceptionsFor(previous, next),
          patternApplied: previous.patternApplied,
        }),
      );
    } catch (err) {
      // Put the real state back rather than leaving the optimistic edit on screen.
      replace(previous);
      setNotice({
        tone: 'error',
        text: err instanceof ApiError ? err.message : "That didn't save. Please try again.",
      });
    }
  }

  /**
   * A second class on the same day — Donald teaches a morning and an afternoon at the same
   * site (SPEC §2.1).
   *
   * The period is what the database uses to tell two same-day slots apart, so the new one
   * takes whichever of morning/afternoon is still free. If both are taken there is nothing
   * left to distinguish a third, and the button is hidden rather than failing on save.
   */
  function addSession(date: string) {
    if (!view) return;
    const onDate = view.occurrences.filter((o) => o.date === date);
    const home = locations.find((l) => l.isHome) ?? locations[0];
    const locationId = onDate[0]?.locationId ?? home?.id ?? 0;
    const taken = new Set(onDate.filter((o) => o.locationId === locationId).map((o) => o.period));
    const period = (['PM', 'AM', 'ALL_DAY'] as const).find((p) => !taken.has(p));
    if (!period) return;

    void persist([
      ...view.occurrences,
      {
        date,
        weekday: onDate[0]?.weekday ?? 'MONDAY',
        period,
        startTime: period === 'AM' ? '09:00' : period === 'PM' ? '13:00' : undefined,
        locationId,
        source: 'ADDITION',
      },
    ]);
  }

  /** How many more slots this day can hold before the unique key runs out. */
  function canAddSession(date: string): boolean {
    if (!view) return false;
    const onDate = view.occurrences.filter((o) => o.date === date);
    if (onDate.length === 0) return false;
    const locationId = onDate[0]!.locationId;
    const taken = new Set(onDate.filter((o) => o.locationId === locationId).map((o) => o.period));
    return taken.size < 3;
  }

  function removeSlot(key: string) {
    if (!view) return;
    void persist(view.occurrences.filter((o) => slotKey(o) !== key));
  }

  /**
   * Tapping a calendar cell. Turning a day OFF clears every slot on it — "I'm out on the
   * 14th" means the whole day, which is also exactly what a date-level REMOVAL does.
   */
  async function toggleDay(date: string, makeAvailable: boolean) {
    if (!view || !today) return;
    if (compare(parseISO(date), today) < 0) return;

    const home = locations.find((l) => l.isHome) ?? locations[0];
    const next: Occurrence[] = makeAvailable
      ? [
          ...view.occurrences,
          {
            date,
            weekday: 'MONDAY',
            period: 'ALL_DAY',
            locationId: home?.id ?? 0,
            source: 'ADDITION',
          },
        ]
      : view.occurrences.filter((o) => o.date !== date);

    await persist(next);
  }

  async function editDay(
    key: string,
    next: { startTime?: string; period: Occurrence['period']; locationId: number },
  ) {
    if (!view) return;
    // Matched on the slot key, not the date. Matching by date rewrote BOTH of Donald's
    // classes whenever he edited one of them.
    const updated: Occurrence[] = view.occurrences.map((o) =>
      slotKey(o) === key
        ? {
            ...o,
            ...next,
            // Dropping startTime matters: switching from "10 AM" back to "All day" must
            // clear the time, not leave a stale one behind.
            ...(next.startTime ? { startTime: next.startTime } : { startTime: undefined }),
            source: 'ADDITION' as const,
          }
        : o,
    );
    await persist(updated);
  }

  async function setConfirmedThrough(value: string | null) {
    if (!view) return;
    replace({ ...view, confirmedThrough: value, horizonApplies: value !== null });
    setConfirming(false);
    try {
      await availability.saveDraft(view.cycleKey, { confirmedThrough: value });
      // The horizon is person-level and spans forward (SPEC §3.3.1), so changing it here
      // changes what every other tab should show. Reloading all of them is one extra
      // request on a rare action, and the alternative is tabs that quietly disagree.
      await load();
    } catch (err) {
      replace(view);
      setNotice({
        tone: 'error',
        text: err instanceof ApiError ? err.message : "That didn't save. Please try again.",
      });
    }
  }

  async function applyPattern() {
    if (!view) return;
    setBusy(true);
    setNotice(null);
    try {
      replace(await availability.applyPattern(view.cycleKey));
    } catch (err) {
      setNotice({
        tone: 'error',
        text: err instanceof ApiError ? err.message : 'Could not apply your usual pattern.',
      });
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!view) return;
    setBusy(true);
    setNotice(null);
    try {
      const result = await availability.submit(view.cycleKey, {
        exceptions: exceptionsFor(view, view.occurrences),
        patternApplied: view.patternApplied,
        note: view.note,
        confirmedThrough: view.confirmedThrough,
      });
      replace(result);
      setConfirming(false);
      setNotice({
        tone: 'success',
        text:
          result.status === 'PENDING_REMAINDER'
            ? voice.subjectName
              ? `Recorded what ${voice.subjectName} knows so far. We'll ask about the rest of ${result.label} later.`
              : `Thanks — we've got what you know so far, and we'll ask about the rest of ${result.label} later.`
            : result.occurrences.length === 0
              ? voice.subjectName
                ? `Recorded that ${voice.subjectName} is not available in ${result.label}.`
                : `Thanks — we've recorded that you're not available in ${result.label}.`
              : voice.subjectName
                ? `${result.label} recorded for ${voice.subjectName}.`
                : `${result.label} is confirmed. Thanks!`,
      });
    } catch (err) {
      setNotice({
        tone: 'error',
        text: err instanceof ApiError ? err.message : 'Could not send that. Please try again.',
      });
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (!view || !today) return <p className="muted">Nothing to show yet.</p>;

  const sorted = [...view.occurrences].sort((a, b) =>
    compare(parseISO(a.date), parseISO(b.date)),
  );
  const monthOnly = view.label.replace(/ \d{4}$/, '');

  // Does the break touch the month being looked at? Overlap, not containment: a break over
  // three weeks of a month is the case that needs explaining most, because the month
  // otherwise looks completely normal.

  // Every break touching this month, for the calendar and the list under it.
  const monthLeaves = leaves.filter(
    (l) => l.startDate <= `${view.cycleKey}-31` && l.endDate >= `${view.cycleKey}-01`,
  );
  // `LeaveSnapshot` omits `endedOn` rather than nulling it; these are all live breaks, so
  // the two dates are the whole story.
  const monthSnapshots = monthLeaves.map((l) => ({
    startDate: l.startDate,
    endDate: l.endDate,
  }));
  const breakInThisMonth = monthLeaves[0] ?? null;

  // One horizon context per render, shared by the calendar, the day list and the summary,
  // so all three cannot disagree about which days are confirmed (SPEC §3.3.3).
  const horizon = horizonContextOf(view);
  const { confirmed, unconfirmed } = splitByHorizon(sorted, horizon);
  const confirmedCount = confirmed.length;

  // Days inside a break are listed but are not cover, so they are counted separately rather
  // than folded into "available" — the same split the coverage grid and the chase view make.
  const awaySlots = sorted.filter((o) => isOnLeaveOn(o.date, monthSnapshots));
  const plannable = sorted.filter((o) => !isOnLeaveOn(o.date, monthSnapshots));
  const plannableCount = splitByHorizon(plannable, horizon).confirmed.length;
  // Distinct dates, which is not the same as slot count once a day can hold two classes.
  const plannableDayCount = new Set(plannable.map((o) => o.date)).size;


  return (
    <div className="stack">
      {/* Above the months, because it changes what the rest of the page means: somebody on
          a break is not expected to fill these in (SPEC §3.7). */}
      {/* Only on the instructor's own screen. An admin manages breaks from the roster row,
          and offering the same action in two places with two different endpoints is how the
          two drift. The month still SHOWS the break either way. */}
      {!voice.subjectName && <LeaveNotice leaves={leaves} onChange={load} />}

      <div className="months" role="tablist" aria-label="Months">
        {months.map((m) => (
          <button
            key={m.cycleKey}
            role="tab"
            className="months__tab"
            aria-current={m.cycleKey === active}
            onClick={() => {
              setActive(m.cycleKey);
              setNotice(null);
              setConfirming(false);
            }}
          >
            {m.label.replace(/ \d{4}$/, '')}
            {m.status === 'COMPLETE' && ' ✓'}
            {/* A part-confirmed month must not wear the same tick as a confirmed one —
                that was the contradiction §3.3.5 exists to remove. */}
            {m.status === 'PENDING_REMAINDER' && ' ~'}
            {m.isRequiredMonth && m.status !== 'COMPLETE' && m.status !== 'PENDING_REMAINDER' && ' •'}
          </button>
        ))}
      </div>

      <div className="card stack">
        <div className="row row--between row--wrap">
          <h1 style={{ margin: 0 }}>{view.label}</h1>
          <StatusBadge status={view.status} label={view.label} />
        </div>

        {view.isRequiredMonth && view.status !== 'COMPLETE' && !breakInThisMonth && (
          <p className="muted">
            This is the month we need from {voice.subjectName || 'you'} next.
          </p>
        )}

        {/* Luke's question, from testing: with a break set, this month still showed his usual
            days in green and nothing said what that meant. The days are deliberately NOT
            rewritten — nothing changes somebody's availability on their behalf (SPEC §3.3.2),
            so they are all still here when the break ends. What was missing was saying so. */}
        {breakInThisMonth && (
          <p className="muted">
            {voice.subjectName || 'You'} {voice.subjectName ? 'is' : 'are'} away{' '}
            <strong>
              {monthLeaves
                .map(
                  (l) =>
                    `${formatDate(parseISO(l.startDate))} to ${formatDate(parseISO(l.endDate))}`,
                )
                .join(', and ')}
            </strong>
            . {voice.subjectName ? 'Their' : 'Your'} usual days still show below and are kept
            for when {voice.subject} are back, but the ones
            inside {voice.possessive} break are not counted as cover, so no class is built on
            them.
          </p>
        )}

        {notice && <Notice tone={notice.tone} text={notice.text} />}

        {/* Without this an edit to an already-confirmed month looks like it did nothing:
            the change is held, but the office has not been told (SPEC §9.1). */}
        {view.hasUnsentChanges && !notice && (
          <Notice
            tone="warn"
            text="You've made changes we haven't been told about yet. Submit again to send them."
          />
        )}

        {/* Tap 1. Never auto-applied: a pattern applied without being seen produces
            confidently-wrong data that looks confirmed (SPEC §9.1).
            
            Gated on the PREVIEW, which the server computes for whoever this month belongs
            to — not on `instructor.hasPattern`, which is the signed-in session and is the
            wrong person on the on-behalf screen (the same mistake as the leave list, STATUS
            decision 53). Luke is signed in as both an admin and an instructor, so the block
            appeared or vanished according to whether HE had a pattern while the button
            applied THEIRS. The preview is also the honest gate: nothing to apply means
            nothing to offer. */}
        {view.patternPreview.length > 0 && !view.patternApplied && (
          <div className="stack">
            <p>
              Your usual {view.label.replace(/ \d{4}$/, '')} would be{' '}
              <strong>{view.patternPreview.length} day{view.patternPreview.length === 1 ? '' : 's'}</strong>.
            </p>
            <button className="btn btn--primary btn--block" onClick={applyPattern} disabled={busy}>
              Apply my usual pattern
            </button>
            {/* Not "nothing is sent until you submit". A saved month is visible to the
                office immediately and shows there as "Started" — the true statement is
                about what it COUNTS as, not about who can see it. */}
            <p className="small muted center">
              {voice.subjectName
                ? `This fills in the calendar. It does not count as their answer until it is submitted.`
                : `This fills in the calendar. It doesn't count as your answer until you submit.`}
            </p>
          </div>
        )}

        <MonthCalendar
          cycleKey={view.cycleKey}
          occurrences={view.occurrences}
          today={today}
          horizon={horizon}
          leaves={monthSnapshots}
          onToggle={toggleDay}
          disabled={busy}
        />
      </div>

      {sorted.length > 0 && (
        <div className="card stack">
          <h2 style={{ margin: 0, fontSize: 'var(--text-lg)' }}>
            {plannableCount === plannable.length
              ? `${plannableDayCount} day${plannableDayCount === 1 ? '' : 's'} available${
                  plannable.length > plannableDayCount ? ` · ${plannable.length} classes` : ''
                }`
              : `${plannableCount} confirmed, ${plannable.length - plannableCount} not yet confirmed`}
            {/* Counted apart from the total, exactly as the admin row does, so the two
                screens cannot tell the same person different things about the same month. */}
            {awaySlots.length > 0 && (
              <span className="small muted"> · {awaySlots.length} inside your break</span>
            )}
          </h2>
          <ul className="daylist">
            {sorted.map((o) => (
              <li
                key={`${o.date}-${o.locationId}-${o.period}`}
                className={`daylist__row${
                  isOnLeaveOn(o.date, monthSnapshots)
                    ? ' daylist__row--break'
                    : isProvisional(o.date, horizon)
                      ? ' daylist__row--provisional'
                      : ''
                }`}
              >
                <span className="daylist__date">
                  {formatDayLong(o.date)}
                  <br />
                  <span className="daylist__meta">
                    {describeSlot(o, locations)}
                    {/* A break outranks the horizon here, the same way it does on the
                        calendar: "not confirmed yet" invites confirming, and there is
                        nothing to confirm on a day you have told us you are away. */}
                    {isOnLeaveOn(o.date, monthSnapshots)
                      ? ' · inside your break'
                      : isProvisional(o.date, horizon) && ' · not confirmed yet'}
                  </span>
                  <DayDetail
                    occurrence={o}
                    locations={locations}
                    disabled={busy}
                    onChange={(next) => void editDay(slotKey(o), next)}
                  />
                  {/* Only on the last row for a date, so a day with two classes offers one
                      "add another", not one per class. */}
                  {canAddSession(o.date) &&
                    sorted.filter((x) => x.date === o.date).at(-1) === o && (
                      <button
                        type="button"
                        className="btn btn--ghost small"
                        style={{ padding: '4px 0', minHeight: 32 }}
                        disabled={busy}
                        onClick={() => addSession(o.date)}
                      >
                        + Add another class this day
                      </button>
                    )}
                </span>
                <button
                  type="button"
                  className="daylist__remove"
                  onClick={() => removeSlot(slotKey(o))}
                  aria-label={
                    sorted.filter((x) => x.date === o.date).length > 1
                      ? `Remove the ${describeSlot(o, locations)} class on ${formatDayLong(o.date)}`
                      : `Remove ${formatDayLong(o.date)}`
                  }
                  disabled={busy}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="card stack">
        <label className="field">
          <span className="field__label">Anything we should know? (optional)</span>
          <textarea
            className="textarea"
            value={view.note ?? ''}
            placeholder="e.g. Available Saturdays but I need to leave by 2pm"
            onChange={(e) => replace({ ...view, note: e.target.value })}
            onBlur={async () => {
              try {
                replace(await availability.saveDraft(view.cycleKey, { note: view.note }));
              } catch {
                /* The note is saved again on submit; a failed autosave is not worth a dialog. */
              }
            }}
          />
        </label>

        <ConfirmedThrough
          cycleKey={view.cycleKey}
          monthName={monthOnly}
          value={view.confirmedThrough}
          applies={view.horizonApplies}
          wholeMonthUnconfirmed={view.wholeMonthUnconfirmed}
          today={today}
          disabled={busy}
          onChange={(value) => void setConfirmedThrough(value)}
        />

        {/* Taps 2 and 3 — Submit then Confirm, with a summary in between so nothing is sent
            without being restated (SPEC §9.1). */}
        {!confirming ? (
          <button
            className="btn btn--primary btn--block"
            onClick={() => setConfirming(true)}
            disabled={busy}
          >
            Submit {view.label.replace(/ \d{4}$/, '')}
          </button>
        ) : (
          <div className="stack">
            <div className="notice notice--info">
              <span className="notice__icon" aria-hidden="true">?</span>
              <span>
                {sorted.length === 0 && !view.horizonApplies ? (
                  <>You're telling us you have <strong>no availability</strong> in {view.label}. That's a complete answer — we won't chase you for it.</>
                ) : view.horizonApplies && view.wholeMonthUnconfirmed ? (
                  /* The §3.3.1 override, stated plainly at the moment it happens. Sending
                     this month IS the statement "I do know October", so it must not be a
                     silent side effect of pressing Submit. */
                  <>
                    Send <strong>{sorted.length} day{sorted.length === 1 ? '' : 's'}</strong> for {view.label}?
                    {' '}This tells us {voice.subject} are sure about all of {monthOnly} —
                    {' '}{voice.subject} will still be
                    down as only sure through <strong>{formatDayLong(view.confirmedThrough!)}</strong>{' '}
                    for the months before it.
                  </>
                ) : view.horizonApplies && view.confirmedThrough ? (
                  <>
                    Send <strong>{confirmedCount} confirmed day{confirmedCount === 1 ? '' : 's'}</strong> through{' '}
                    <strong>{formatDayLong(view.confirmedThrough)}</strong>
                    {unconfirmed.length > 0 && (
                      <>, plus {unconfirmed.length} we'll confirm later</>
                    )}
                    ?
                  </>
                ) : (
                  <>Send <strong>{sorted.length} day{sorted.length === 1 ? '' : 's'}</strong> for {view.label}?</>
                )}
              </span>
            </div>

            {/* Both instructors who prompted this were first-time users, so the same
                review renders in the onboarding wizard. See DefaultsReview. */}
            <DefaultsReview
              occurrences={plannable}
              locations={locations}
              {...(voice.subjectName ? { subjectName: voice.subjectName } : {})}
            />

            <div className="row">
              <button className="btn btn--primary grow" onClick={submit} disabled={busy}>
                {busy ? 'Sending…' : 'Confirm'}
              </button>
              <button className="btn btn--secondary" onClick={() => setConfirming(false)} disabled={busy}>
                Back
              </button>
            </div>
          </div>
        )}

        {view.status === 'COMPLETE' && !view.hasUnsentChanges && (
          <p className="small muted center">
            You can change this any time — just resubmit. We'll let the office know.
          </p>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status, label }: { status: MonthView['status']; label: string }) {
  const month = label.replace(/ \d{4}$/, '');
  const map = {
    COMPLETE: { cls: 'complete', icon: '✓', text: `${month} is confirmed` },
    PENDING_REMAINDER: { cls: 'partial', icon: '~', text: `${month} is partly confirmed` },
    PARTIAL: { cls: 'partial', icon: '•', text: 'Saved, not sent yet' },
    ON_LEAVE: { cls: 'none', icon: '⏸', text: "You're on a break" },
    NO_RESPONSE: { cls: 'none', icon: '', text: 'Not sent yet' },
  } as const;
  const badge = map[status];
  return (
    <span className={`badge badge--${badge.cls}`}>
      {badge.icon && <span aria-hidden="true">{badge.icon}</span>}
      {badge.text}
    </span>
  );
}

function Notice({ tone, text }: { tone: Tone; text: string }) {
  const icon = { info: 'ℹ', warn: '!', error: '!', success: '✓' }[tone];
  return (
    <div className={`notice notice--${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <span className="notice__icon" aria-hidden="true">{icon}</span>
      <span>{text}</span>
    </div>
  );
}
